import type { MissionAssessmentListItemDto, MissionAssessmentRequestSelectDto } from '@@types/api.types';
import { MissionAssessmentRequestStatus } from '@@types/api.types';

export type CoverageRow = {
  assessment?: MissionAssessmentListItemDto;
  domainId: string;
  domainName: string;
  isAssignedToYou: boolean;
  latestRequest?: MissionAssessmentRequestSelectDto;
  status: 'Assessed' | 'In Progress' | 'None' | 'Requested';
  useId: string;
  useName: string;
};

export const isOpenRequestStatus = (status: MissionAssessmentRequestStatus): boolean => {
  return status === MissionAssessmentRequestStatus.IN_PROGRESS || status === MissionAssessmentRequestStatus.REQUESTED;
};

export const sortByCreatedAtDescending = <T extends { createdAt: string }>(left: T, right: T): number => {
  return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
};

export const sortByUpdatedAtDescending = <T extends { updatedAt: string }>(left: T, right: T): number => {
  return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
};

export const getRequestSortWeight = (status: MissionAssessmentRequestStatus): number => {
  if (isOpenRequestStatus(status)) {
    return 0;
  }

  if (status === MissionAssessmentRequestStatus.ASSESSED) {
    return 1;
  }

  return 2;
};

const normalizeCoverageLabel = (value: string): string => {
  return value.trim().toLowerCase();
};

const getCoverageKey = (params: { domainId: string; useId: string }): string => {
  const { domainId, useId } = params;
  return `${useId}:${domainId}`;
};

const getCoverageLabelKey = (params: { domainName: string; useName: string }): string => {
  const { domainName, useName } = params;
  return `${normalizeCoverageLabel(useName)}:${normalizeCoverageLabel(domainName)}`;
};

export const sortCoverageRows = (left: CoverageRow, right: CoverageRow): number => {
  const useSort = normalizeCoverageLabel(left.useName).localeCompare(normalizeCoverageLabel(right.useName));

  if (useSort !== 0) {
    return useSort;
  }

  return normalizeCoverageLabel(left.domainName).localeCompare(normalizeCoverageLabel(right.domainName));
};

const getRequestCoverageKey = (request: MissionAssessmentRequestSelectDto): string => {
  return getCoverageKey({
    domainId: request.domain.id,
    useId: request.use.id,
  });
};

const getRequestCoverageLabelKey = (request: MissionAssessmentRequestSelectDto): string => {
  return getCoverageLabelKey({
    domainName: request.domain.name,
    useName: request.use.name,
  });
};

const getAssessmentCoverageKey = (assessment: MissionAssessmentListItemDto): string | undefined => {
  const domainId = assessment.request?.domain?.id;
  const useId = assessment.request?.use?.id;

  if (!domainId || !useId) {
    return undefined;
  }

  return getCoverageKey({ domainId, useId });
};

const getAssessmentCoverageLabelKey = (assessment: MissionAssessmentListItemDto): string | undefined => {
  const domainName = assessment.request?.domain?.name;
  const useName = assessment.request?.use?.name;

  if (!domainName || !useName) {
    return undefined;
  }

  return getCoverageLabelKey({ domainName, useName });
};

const getCoverageStatus = (params: {
  latestAssessment?: MissionAssessmentListItemDto;
  requests: MissionAssessmentRequestSelectDto[];
}): CoverageRow['status'] => {
  const { latestAssessment, requests } = params;
  const assessedRequest = requests.find((request) => request.status === MissionAssessmentRequestStatus.ASSESSED);
  const inProgressRequest = requests.find((request) => request.status === MissionAssessmentRequestStatus.IN_PROGRESS);
  const requestedRequest = requests.find((request) => request.status === MissionAssessmentRequestStatus.REQUESTED);

  if (inProgressRequest) {
    return 'In Progress';
  }

  if (requestedRequest) {
    return 'Requested';
  }

  if (latestAssessment || assessedRequest) {
    return 'Assessed';
  }

  return 'None';
};

const buildCoverageRow = (params: {
  assessments: MissionAssessmentListItemDto[];
  currentUserId?: string;
  domainId: string;
  domainName: string;
  isExpert: boolean;
  requests: MissionAssessmentRequestSelectDto[];
  useId: string;
  useName: string;
}): CoverageRow => {
  const { assessments, currentUserId, domainId, domainName, isExpert, requests, useId, useName } = params;
  const latestAssessment = assessments[0];
  const latestRequest = requests[0];

  return {
    assessment: latestAssessment,
    domainId,
    domainName,
    isAssignedToYou: Boolean(isExpert && currentUserId && latestAssessment?.expertId === currentUserId),
    latestRequest,
    status: getCoverageStatus({ latestAssessment, requests }),
    useId,
    useName,
  };
};

const getLatestActivityAt = (params: {
  latestAssessment?: MissionAssessmentListItemDto;
  latestRequest?: MissionAssessmentRequestSelectDto;
}): string => {
  const { latestAssessment, latestRequest } = params;
  const assessmentUpdatedAt = latestAssessment ? new Date(latestAssessment.updatedAt).getTime() : 0;
  const requestCreatedAt = latestRequest ? new Date(latestRequest.createdAt).getTime() : 0;

  if (assessmentUpdatedAt >= requestCreatedAt) {
    return latestAssessment?.updatedAt ?? latestRequest?.createdAt ?? '';
  }

  return latestRequest?.createdAt ?? latestAssessment?.updatedAt ?? '';
};

export type BuildCoverageRowsParams = {
  currentUserId?: string;
  isExpert: boolean;
  requestsByCreatedAt: MissionAssessmentRequestSelectDto[];
  sortedVersions: MissionAssessmentListItemDto[];
  technologyUses: Array<{
    domains: Array<{ id: string; name: string; position: number }>;
    name: string;
    position: number;
    useId: string;
  }>;
};

export const buildExpertCoverageRows = (params: {
  sortedVersions: MissionAssessmentListItemDto[];
}): CoverageRow[] => {
  const { sortedVersions } = params;
  const expertRowsByCoverageKey = new Map<string, CoverageRow>();

  for (const version of sortedVersions) {
    const domainId = version.request?.domain?.id;
    const domainName = version.request?.domain?.name;
    const useId = version.request?.use?.id;
    const useName = version.request?.use?.name;

    if (!domainId || !domainName || !useId || !useName) {
      continue;
    }

    const coverageKey = getAssessmentCoverageLabelKey(version) ?? getCoverageKey({ domainId, useId });

    if (expertRowsByCoverageKey.has(coverageKey)) {
      continue;
    }

    expertRowsByCoverageKey.set(coverageKey, {
      assessment: version,
      domainId,
      domainName,
      isAssignedToYou: false,
      latestRequest: undefined,
      status: 'Assessed',
      useId,
      useName,
    });
  }

  return [...expertRowsByCoverageKey.values()].sort(sortCoverageRows);
};

export const buildInnovatorCoverageRows = (params: BuildCoverageRowsParams): CoverageRow[] => {
  const { currentUserId, isExpert, requestsByCreatedAt, sortedVersions, technologyUses } = params;

  const matchedAssessmentIds = new Set<string>();
  const matchedRequestIds = new Set<string>();
  const rows: CoverageRow[] = [];
  const sortedUses = [...technologyUses].sort((left, right) => left.position - right.position);

  for (const technologyUse of sortedUses) {
    const sortedDomains = [...technologyUse.domains].sort((left, right) => left.position - right.position);

    for (const domain of sortedDomains) {
      const coverageIdKey = getCoverageKey({
        domainId: domain.id,
        useId: technologyUse.useId,
      });
      const coverageLabelKey = getCoverageLabelKey({
        domainName: domain.name,
        useName: technologyUse.name,
      });
      const requests = requestsByCreatedAt.filter((request) => {
        if (matchedRequestIds.has(request.id)) return false;

        const requestCoverageKey = getRequestCoverageKey(request);
        const requestCoverageLabelKey = getRequestCoverageLabelKey(request);
        const matchesCoverage = requestCoverageKey === coverageIdKey || requestCoverageLabelKey === coverageLabelKey;

        if (matchesCoverage) {
          matchedRequestIds.add(request.id);
        }

        return matchesCoverage;
      });
      const assessments = sortedVersions.filter((version) => {
        if (matchedAssessmentIds.has(version.id)) return false;

        const assessmentCoverageKey = getAssessmentCoverageKey(version);
        const assessmentCoverageLabelKey = getAssessmentCoverageLabelKey(version);
        const matchesCoverage =
          assessmentCoverageKey === coverageIdKey || assessmentCoverageLabelKey === coverageLabelKey;

        if (matchesCoverage) {
          matchedAssessmentIds.add(version.id);
        }

        return matchesCoverage;
      });

      rows.push(
        buildCoverageRow({
          assessments,
          currentUserId,
          domainId: domain.id,
          domainName: domain.name,
          isExpert,
          requests,
          useId: technologyUse.useId,
          useName: technologyUse.name,
        }),
      );
    }
  }

  const uncoveredByCoverageLabelKey = new Map<
    string,
    {
      assessments: MissionAssessmentListItemDto[];
      domainId: string;
      domainName: string;
      requests: MissionAssessmentRequestSelectDto[];
      useId: string;
      useName: string;
    }
  >();

  for (const request of requestsByCreatedAt) {
    if (matchedRequestIds.has(request.id)) {
      continue;
    }

    const coverageLabelKey = getRequestCoverageLabelKey(request);
    const uncoveredRow = uncoveredByCoverageLabelKey.get(coverageLabelKey);

    if (uncoveredRow) {
      uncoveredRow.requests.push(request);
      continue;
    }

    uncoveredByCoverageLabelKey.set(coverageLabelKey, {
      assessments: [],
      domainId: request.domain.id,
      domainName: request.domain.name,
      requests: [request],
      useId: request.use.id,
      useName: request.use.name,
    });
  }

  for (const version of sortedVersions) {
    if (matchedAssessmentIds.has(version.id)) {
      continue;
    }

    const coverageLabelKey = getAssessmentCoverageLabelKey(version);
    const domainId = version.request?.domain?.id;
    const domainName = version.request?.domain?.name;
    const useId = version.request?.use?.id;
    const useName = version.request?.use?.name;

    if (!coverageLabelKey || !domainId || !domainName || !useId || !useName) {
      continue;
    }

    const uncoveredRow = uncoveredByCoverageLabelKey.get(coverageLabelKey);

    if (uncoveredRow) {
      uncoveredRow.assessments.push(version);
      continue;
    }

    uncoveredByCoverageLabelKey.set(coverageLabelKey, {
      assessments: [version],
      domainId,
      domainName,
      requests: [],
      useId,
      useName,
    });
  }

  const uncoveredRows = [...uncoveredByCoverageLabelKey.values()]
    .map((uncoveredRow) => {
      const latestAssessment = uncoveredRow.assessments[0];
      const latestRequest = uncoveredRow.requests[0];

      return {
        lastActivityAt: getLatestActivityAt({ latestAssessment, latestRequest }),
        row: buildCoverageRow({
          assessments: uncoveredRow.assessments,
          currentUserId,
          domainId: uncoveredRow.domainId,
          domainName: uncoveredRow.domainName,
          isExpert,
          requests: uncoveredRow.requests,
          useId: uncoveredRow.useId,
          useName: uncoveredRow.useName,
        }),
      };
    })
    .sort((left, right) => {
      return new Date(right.lastActivityAt).getTime() - new Date(left.lastActivityAt).getTime();
    })
    .map(({ row }) => row);

  rows.push(...uncoveredRows);

  return rows.sort(sortCoverageRows);
};
